import { useEffect, useState } from 'react';
import apiClient from '@/services/api/client';
import type { MockUser } from '@/mocks';
import { useUser } from '@/features/auth/hooks';
import { useThemeStore } from '@/features/theme/store';
import { SUPPORTED_LANGUAGES, resolveSupportedLanguage } from '@/features/language/config';
import { useLanguageStore } from '@/features/language/store';
import { useShellI18n } from '@/features/language/useI18n';

interface LocalSettings {
  notifications: boolean;
}

interface FeedbackState {
  type: 'success' | 'error';
  message: string;
}

const getRoleLabel = (role: MockUser['role'] | undefined, t: (message: string) => string) => {
  if (role === 'admin') {
    return t('Administrator');
  }

  if (role === 'partner') {
    return t('Partner');
  }

  if (role === 'user') {
    return t('User');
  }

  return t('guest');
};

export const SettingsPage = () => {
  const { user: authUser } = useUser();
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);
  const language = useLanguageStore((state) => state.language);
  const setLanguage = useLanguageStore((state) => state.setLanguage);
  const { t } = useShellI18n();
  const [profile, setProfile] = useState<MockUser | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [settings, setSettings] = useState<LocalSettings>(() => ({
    notifications: true,
  }));
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setProfileLoading(true);
        const response = await apiClient().get('/user/profile');
        const loadedUser = response.data as MockUser;
        setProfile(loadedUser);
        setSettings(() => ({
          notifications: loadedUser.settings.notifications,
        }));
        if (loadedUser.settings.theme === 'light' || loadedUser.settings.theme === 'dark') {
          setTheme(loadedUser.settings.theme);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchUser();
  }, [setTheme]);

  const handleSettingsChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;

    if (name === 'theme') {
      setTheme(value === 'light' ? 'light' : 'dark');
      return;
    }

    if (name === 'language') {
      setLanguage(resolveSupportedLanguage(value));
      return;
    }

    const resolvedValue = type === 'checkbox' ? (event.target as HTMLInputElement).checked : value;
    setSettings((current) => ({
      ...current,
      notifications: Boolean(resolvedValue),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!profile) {
      setFeedback({ type: 'success', message: 'Preferences applied locally.' });
      return;
    }

    try {
      setFeedback(null);
      await apiClient().post('/settings', {
        ...profile.settings,
        theme,
        language,
        notifications: settings.notifications,
      });
      setFeedback({ type: 'success', message: 'Settings saved successfully.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'Failed to save settings. Preferences are still applied locally.',
      });
      console.error(error);
    }
  };

  const profileName = profile?.name ?? authUser?.email?.split('@')[0] ?? t('Unavailable');
  const profileEmail = profile?.email ?? authUser?.email ?? t('Unavailable');
  const profileRole = profile
    ? getRoleLabel(profile.role, t)
    : t(authUser ? 'authenticated' : 'guest');

  return (
    <section className="page-shell">
      <div className="page-hero">
        <h1 className="page-title">{t('Workspace Settings')}</h1>
        <p className="page-subtitle">{t('Configure workspace preferences, language, visual theme and notification defaults.')}</p>
      </div>

      {feedback && (
        <div className={`feedback ${feedback.type}`}>{t(feedback.message)}</div>
      )}

      <form className="settings-form" onSubmit={handleSubmit}>
        <article className="panel span-12">
          <div className="panel-header">
            <h2 className="panel-title">{t('Profile')}</h2>
            {profileLoading && <span className="meta">{t('Loading profile...')}</span>}
          </div>
          <div className="field">
            <label>{t('Name')}</label>
            <input value={profileName} disabled />
          </div>
          <div className="field">
            <label>{t('Email')}</label>
            <input value={profileEmail} disabled />
          </div>
          <div className="field">
            <label>{t('Role')}</label>
            <input value={profileRole} disabled />
          </div>
        </article>

        <article className="panel span-12">
          <div className="panel-header">
            <h2 className="panel-title">{t('Appearance and Notifications')}</h2>
          </div>

          <div className="field">
            <label htmlFor="language">{t('Language')}</label>
            <select id="language" name="language" value={language} onChange={handleSettingsChange}>
              {SUPPORTED_LANGUAGES.map((option) => (
                <option key={option.code} value={option.code}>{t(option.label)}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="theme">{t('Theme')}</label>
            <select id="theme" name="theme" value={theme} onChange={handleSettingsChange}>
              <option value="dark">{t('Dark')}</option>
              <option value="light">{t('Light')}</option>
            </select>
          </div>

          <label className="checkbox-row">
            <input
              type="checkbox"
              name="notifications"
              checked={settings.notifications}
              onChange={handleSettingsChange}
            />
            {t('Enable email notifications')}
          </label>

          <div className="form-actions">
            <button type="submit" className="btn">{t('Save Settings')}</button>
          </div>
        </article>
      </form>
    </section>
  );
};

export default SettingsPage;
